import React from "react";

interface Props {
  /** When true, renders just the SVG (for table cells, buttons, inline labels).
   *  Default: renders the SVG inside a centered block with reserved vertical space
   *  so first paint is stable and the spinner doesn't jump from top-left to center
   *  as the surrounding layout (sidebar/topbar) finishes hydrating. */
  inline?: boolean;
  /** Pixel size of the SVG itself. Default 36. */
  size?: number;
  /** Minimum block height when not inline. Default 240. */
  minHeight?: number;
  /** After this many ms still spinning, show "Taking longer than usual…" + Retry.
   *  A swallowed fetch error can leave a spinner up forever — this is the escape hatch.
   *  Default 12000. Set 0 to disable. */
  stuckAfterMs?: number;
}

function Svg({ size = 36 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 36 36"
      xmlns="http://www.w3.org/2000/svg"
      style={{ animation: "v-spin 0.9s linear infinite" }}
    >
      <style>{`@keyframes v-spin { to { transform: rotate(360deg); } }`}</style>
      <circle cx="18" cy="18" r="15" fill="none" stroke="#e5e7eb" strokeWidth="3" />
      <circle
        cx="18"
        cy="18"
        r="15"
        fill="none"
        stroke="#4C75AB"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="70 30"
      />
    </svg>
  );
}

export default function LoadingSpinner({ inline = false, size, minHeight = 240, stuckAfterMs = 12000 }: Props) {
  const [stuck, setStuck] = React.useState(false);
  React.useEffect(() => {
    if (inline || !stuckAfterMs) return;
    const t = window.setTimeout(() => setStuck(true), stuckAfterMs);
    return () => window.clearTimeout(t);
  }, [inline, stuckAfterMs]);

  if (inline) return <Svg size={size} />;
  return (
    <div className="flex flex-col items-center justify-center w-full gap-3" style={{ minHeight }}>
      <Svg size={size} />
      {stuck && (
        <div style={{ textAlign: "center", maxWidth: 320 }}>
          <div style={{ color: "#374151", fontSize: 13, marginBottom: 8 }}>
            Taking longer than usual…
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background: "#4C75AB",
              color: "white",
              border: "none",
              borderRadius: 6,
              padding: "6px 16px",
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
